import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import { useAuth } from "@/auth/useAuth";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useInviteUser } from "@/features/users/hooks/useUsers";
import { cn } from "@/lib/utils";

interface InviteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InviteUserDialog({ open, onOpenChange }: InviteUserDialogProps) {
  const { t } = useTranslation("users");
  const { t: tc } = useTranslation("common");
  const { isAdmin } = useAuth();
  const invite = useInviteUser();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [isAdminFlag, setIsAdminFlag] = useState(false);
  const [piiFilter, setPiiFilter] = useState(false);

  function reset() {
    setEmail("");
    setName("");
    setIsAdminFlag(false);
    setPiiFilter(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      const user = await invite.mutateAsync({
        email,
        name,
        // is_admin so e enviado se o requester for admin -- backend ja
        // valida, mas evitar payload inutil.
        is_admin: isAdmin ? isAdminFlag : undefined,
        pii_filter_enabled: piiFilter || undefined,
      });
      toast.success(t("toasts.invited", { email: user.email }));
      reset();
      onOpenChange(false);
    } catch (err) {
      toastApiError(err);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("inviteDialog.title")}</DialogTitle>
          <DialogDescription>{t("inviteDialog.description")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="invite-email">{t("fields.email")}</Label>
            <Input
              id="invite-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="invite-name">{t("fields.name")}</Label>
            <Input
              id="invite-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-2 rounded-md border p-3">
            <p className="text-xs font-semibold uppercase text-muted-foreground">
              {t("inviteDialog.initialPermissions")}
            </p>
            <label
              className={cn(
                "flex items-start gap-2 text-sm",
                !isAdmin && "cursor-not-allowed opacity-60",
              )}
            >
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4"
                checked={isAdminFlag}
                onChange={(e) => setIsAdminFlag(e.target.checked)}
                disabled={!isAdmin}
              />
              <span className="flex flex-col">
                <span className="font-medium">
                  {t("inviteDialog.globalAdmin")}
                </span>
                <span className="text-xs text-muted-foreground">
                  {isAdmin
                    ? t("inviteDialog.globalAdminHint")
                    : t("inviteDialog.globalAdminDisabled")}
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4"
                checked={piiFilter}
                onChange={(e) => setPiiFilter(e.target.checked)}
              />
              <span className="flex flex-col">
                <span className="font-medium">
                  {t("inviteDialog.piiFilter")}
                </span>
                <span className="text-xs text-muted-foreground">
                  {t("inviteDialog.piiFilterHint")}
                </span>
              </span>
            </label>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {tc("actions.cancel")}
            </Button>
            <Button type="submit" disabled={invite.isPending}>
              {t("inviteDialog.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
